import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Typography, Popover, TextField, Stack, Divider, Grid } from '@vapor/material'
import { Button, Tooltip } from '@mui/material'
import React, { useRef, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NoteType } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useUpdateReservationNote from '../../api/update-note.hook'
import { HiCIcon } from 'src/app/components/icons/icon'
import _ from 'lodash'
import { ReservationF } from '../../types/reservation-detail.types'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { EditableBox } from '../header-components.styled'
import { noteIcon } from './note-icon'

type noteProps = {
  readOnly: boolean
  reservation: ReservationF
  allowCheckinNote?: boolean
  allowCheckoutNote?: boolean
  allowHousekeepingNote?: boolean
  allowRestaurantNote?: boolean
  allowPortalNote?: boolean
}

export function Note({ readOnly, reservation, allowCheckinNote, allowCheckoutNote, allowHousekeepingNote, allowRestaurantNote, allowPortalNote }: noteProps) {
  const showError = useErrorSnackBar()
  const ref = useRef(null)
  const { t } = useTranslation()
  const updateNotes = useUpdateReservationNote()
  const { lg, md } = useBreakpoints()

  const notes = useMemo(() => {
    return _.chain(reservation.notes).keyBy('type').mapValues('description').value()
  }, [reservation.notes])

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [textFieldNoteReservation, setTextFieldNoteReservation] = useState('')
  const [textFieldNoteCheckin, setTextFieldNoteCheckin] = useState('')
  const [textFieldNoteCheckout, setTextFieldNoteCheckout] = useState('')
  const [textFieldNoteHousekeeping, setTextFieldNoteHousekeeping] = useState('')
  const [textFieldNotePortal, setTextFieldNotePortal] = useState('')
  const [textFieldNoteRestaurant, setTextFieldNoteRestaurant] = useState('')

  const openPopover = useCallback(
    (element: HTMLDivElement) => {
      setTextFieldNoteReservation(notes[NoteType.RESERVATION] || '')
      setTextFieldNoteCheckin(notes[NoteType.CHECKIN] || '')
      setTextFieldNoteCheckout(notes[NoteType.CHECKOUT] || '')
      setTextFieldNoteHousekeeping(notes[NoteType.HOUSEKEEPING] || '')
      setTextFieldNoteRestaurant(notes[NoteType.RESTAURANT] || '')
      setTextFieldNotePortal(notes[NoteType.PORTAL] || '')
      setAnchorEl(element)
    },
    [notes],
  )

  const closePopover = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const onConfirmClick = useCallback(async () => {
    const result = await updateNotes({
      ...reservation,
      notes: [
        { type: NoteType.CHECKIN, description: textFieldNoteCheckin },
        { type: NoteType.CHECKOUT, description: textFieldNoteCheckout },
        { type: NoteType.HOUSEKEEPING, description: textFieldNoteHousekeeping },
        { type: NoteType.RESERVATION, description: textFieldNoteReservation },
        { type: NoteType.RESTAURANT, description: textFieldNoteRestaurant },
        { type: NoteType.PORTAL, description: textFieldNotePortal },
      ],
    })
    if (result.type === 'SUCCESS') {
      closePopover()
    } else {
      showError(result.errors)
    }

    closePopover()
  }, [reservation, textFieldNoteReservation, textFieldNoteCheckin, textFieldNoteCheckout, textFieldNoteHousekeeping, textFieldNoteRestaurant, textFieldNotePortal])

  const noteColumns = useMemo(() => {
    // const n = reservation.notes?.filter((x) => x.description !== '' || x.type === 'RESERVATION')?.length ?? 1
    if (lg) {
      return 3
    } else if (md) {
      return 2
    } else {
      return 1
    }
  }, [lg, md, reservation.notes])

  return (
    <div ref={ref} style={{ pointerEvents: readOnly ? 'none' : undefined }}>
      <Grid container columns={noteColumns} spacing={1}>
        {reservation.notes && reservation.notes.length > 0 ? (
          reservation.notes
            .filter((n) => n.description !== '' || n.type === 'RESERVATION')
            .map((note, key) => (
              <Grid item xs={1} key={key} onClick={(event) => openPopover(event.currentTarget)}>
                <NoteElement {...note} />
              </Grid>
            ))
        ) : (
          <Grid item xs={1} onClick={(event) => openPopover(event.currentTarget)}>
            <NoteElement description="" type="RESERVATION" />
          </Grid>
        )}
      </Grid>
      <Popover
        open={anchorEl ? true : false}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: { sx: { width: { xs: '100%', md: '50%', lg: '25%' } } },
        }}
      >
        <Stack direction="column" spacing={1} style={{ padding: 10 }}>
          <Stack direction="row">
            <HiCIcon sx={{ pr: 1 }}>
              <FontAwesomeIcon icon={noteIcon(NoteType.RESERVATION)} />
            </HiCIcon>
            <Typography variant="caption">{t('pms.booking.reservation.note.field_label_reservation')}</Typography>
          </Stack>
          <TextField fullWidth defaultValue={textFieldNoteReservation} onChange={(v) => setTextFieldNoteReservation(v.target.value)} multiline />
          {allowCheckinNote && (
            <>
              <Stack direction="row">
                <HiCIcon sx={{ pr: 1 }}>
                  <FontAwesomeIcon icon={noteIcon(NoteType.CHECKIN)} />
                </HiCIcon>
                <Typography variant="caption">{t('pms.booking.reservation.note.field_label_checkin')}</Typography>
              </Stack>
              <TextField fullWidth defaultValue={textFieldNoteCheckin} onChange={(v) => setTextFieldNoteCheckin(v.target.value)} multiline />
            </>
          )}
          {allowCheckoutNote && (
            <>
              <Stack direction="row">
                <HiCIcon sx={{ pr: 1 }}>
                  <FontAwesomeIcon icon={noteIcon(NoteType.CHECKOUT)} />
                </HiCIcon>
                <Typography variant="caption">{t('pms.booking.reservation.note.field_label_checkout')}</Typography>
              </Stack>
              <TextField fullWidth defaultValue={textFieldNoteCheckout} onChange={(v) => setTextFieldNoteCheckout(v.target.value)} multiline />
            </>
          )}
          {allowHousekeepingNote && (
            <>
              <Stack direction="row">
                <HiCIcon sx={{ pr: 1 }}>
                  <FontAwesomeIcon icon={noteIcon(NoteType.HOUSEKEEPING)} />
                </HiCIcon>
                <Typography variant="caption">{t('pms.booking.reservation.note.field_label_housekeeping')}</Typography>
              </Stack>
              <TextField fullWidth defaultValue={textFieldNoteHousekeeping} onChange={(v) => setTextFieldNoteHousekeeping(v.target.value)} multiline />
            </>
          )}
          {allowRestaurantNote && (
            <>
              <Stack direction="row">
                <HiCIcon sx={{ pr: 1 }}>
                  <FontAwesomeIcon icon={noteIcon(NoteType.RESTAURANT)} />
                </HiCIcon>
                <Typography variant="caption">{t('pms.booking.reservation.note.field_label_restaurant')}</Typography>
              </Stack>
              <TextField fullWidth defaultValue={textFieldNoteRestaurant} onChange={(v) => setTextFieldNoteRestaurant(v.target.value)} multiline />
            </>
          )}
          {allowPortalNote && textFieldNotePortal !== '' && (
            <>
              <Stack direction="row">
                <HiCIcon sx={{ pr: 1 }}>
                  <FontAwesomeIcon icon={noteIcon(NoteType.PORTAL)} />
                </HiCIcon>
                <Typography variant="caption">{t('pms.booking.reservation.note.field_label_portal')}</Typography>
              </Stack>
              <Typography variant="body">{textFieldNotePortal}</Typography>
            </>
          )}
          <Divider />
          <Stack direction="row-reverse" spacing={1}>
            <Button onClick={onConfirmClick} variant="contained">
              {t('commons.save')}
            </Button>
            <Button onClick={closePopover}>{t('commons.cancel')}</Button>
          </Stack>
        </Stack>
      </Popover>
    </div>
  )
}

type NoteElementProps = {
  description: string
  type: NoteType
  placeholder?: string
}

const NoteElement = (props: NoteElementProps) => {
  const { t } = useTranslation()
  return (
    <Tooltip title={t('pms.booking.reservation.note_tooltip')} arrow>
      <span>
        <EditableBox>
          <Stack direction="row" paddingY={0.25} spacing={1}>
            <HiCIcon width={'1rem'}>
              <FontAwesomeIcon icon={noteIcon(props.type)} />
            </HiCIcon>
            {props.description !== '' ? (
              <Typography
                variant="caption"
                sx={{
                  whiteSpace: 'pre-line',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {props.description}
              </Typography>
            ) : (
              <Typography variant="caption" color="gray" whiteSpace={'pre-line'}>
                {props.placeholder ?? `${t('pms.booking.reservation.note_label')}...`}
              </Typography>
            )}
          </Stack>
        </EditableBox>
      </span>
    </Tooltip>
  )
}
